import { useEffect, useRef, useState, type PointerEvent, type ReactNode } from "react";
import {
  defaultComposerPlacement,
  offsetComposerPosition,
  type ComposerPosition
} from "./composer-position.js";
import type { ComposerLayout } from "./composer-layout.js";

export type { ComposerLayout } from "./composer-layout.js";

export interface SeedanceComposerOverlayProps {
  nodeId: string;
  node: {
    canvasPosition: { x: number; y: number };
    size: { width: number; height: number };
  };
  flowViewport: { x: number; y: number; zoom: number };
  canvasViewport: { width: number; height: number };
  layout: ComposerLayout;
  host?: "canvas" | "node";
  positionOffset?: ComposerPosition;
  children: ReactNode;
}

const cardSize = { width: 520, height: 360 };

export function SeedanceComposerOverlay({
  nodeId,
  node,
  flowViewport,
  canvasViewport,
  layout,
  host = "canvas",
  positionOffset,
  children
}: SeedanceComposerOverlayProps) {
  const placement = defaultComposerPlacement({ node, flowViewport, canvasViewport, card: cardSize });
  const base = { left: placement.left, top: placement.top };
  const [offset, setOffset] = useState<ComposerPosition>(positionOffset ?? { left: 0, top: 0 });
  const placementCard = { width: placement.width, height: placement.height };
  const clampedOffset = layout === "floating" && host !== "node"
    ? offsetComposerPosition({
        base,
        offset,
        delta: { x: 0, y: 0 },
        viewport: canvasViewport,
        card: placementCard
      })
    : offset;
  const dragRef = useRef<{ pointerId: number; start: { x: number; y: number }; offset: ComposerPosition } | undefined>(undefined);
  useEffect(() => {
    if (layout !== "floating" || host === "node") return;
    setOffset((current) => {
      const next = offsetComposerPosition({
        base,
        offset: current,
        delta: { x: 0, y: 0 },
        viewport: canvasViewport,
        card: placementCard
      });
      return next.left === current.left && next.top === current.top ? current : next;
    });
  }, [base.left, base.top, canvasViewport.height, canvasViewport.width, host, layout, placementCard.height, placementCard.width]);
  const startDrag = (event: PointerEvent<HTMLElement>) => {
    if (
      layout !== "floating" ||
      host === "node" ||
      event.button !== 0 ||
      !(event.target instanceof Element) ||
      !event.target.closest("[data-composer-drag-handle]")
    ) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      start: { x: event.clientX, y: event.clientY },
      offset: clampedOffset
    };
  };
  const moveDrag = (event: PointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    setOffset(offsetComposerPosition({
      base,
      offset: drag.offset,
      delta: { x: event.clientX - drag.start.x, y: event.clientY - drag.start.y },
      viewport: canvasViewport,
      card: placementCard
    }));
  };
  const finishDrag = (event: PointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = undefined;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };
  const style = layout === "floating"
    ? host === "node"
      ? { width: "100%", height: "100%", maxHeight: "100%" }
      : {
          left: base.left + clampedOffset.left,
          top: base.top + clampedOffset.top,
          width: placement.width,
          maxHeight: placement.height
        }
    : undefined;
  const overlayClassName = `studio-seedance-composer-overlay${layout === "floating" ? " studio-seedance-composer-overlay--floating" : ""}${host === "node" ? " studio-seedance-composer-overlay--node" : ""}`;
  return (
    <aside
      className={overlayClassName}
      aria-label="Seedance 生成卡片"
      data-seedance-node-id={nodeId}
      data-layout={layout}
      data-host={host}
      style={style}
      onPointerDown={startDrag}
      onPointerMove={moveDrag}
      onPointerUp={finishDrag}
      onPointerCancel={finishDrag}
    >
      {children}
    </aside>
  );
}
